<template>
  <div class="analytic-distribution">
    <header class="analytic-distribution__header">
      <div class="analytic-distribution__header__title" v-if="chart.chart_distribution.title">
        {{ chart.chart_distribution.title[$i18n.locale] || chart.chart_distribution.title.default }}
      </div>
      <div
        class="analytic-distribution__header__tooltip backoffice-icons"
        v-tooltip="{
          placement: 'top',
          trigger: 'hover click',
          content: chart.chart_distribution.description[$i18n.locale] || chart.chart_distribution.description.default,
          offset: 3,
        }"
        v-if="chart.chart_distribution.description"
      >
        info
      </div>
    </header>
    <div class="analytic-distribution__legend" v-if="chart.chart_distribution.legend">
      <ui-legend :legends="chart.chart_distribution.legend" />
    </div>
    <div class="analytic-distribution__container">
      <ui-chart class="analytic-distribution__container__chart" id="chart-distribution" :options="options" />
    </div>
  </div>
</template>

<script>
import UiLegend from '@/components/UI/Legend.vue'
import UiChart from '@/components/UI/Chart.vue'

const chartFontFamily = 'OpenSans'
const chartFontSize = 11

export default {
  name: 'AnalyticDistribution',
  components: {
    UiChart,
    UiLegend,
  },
  props: {
    chart: {
      type: Object,
      required: true,
    },
  },
  watch: {
    chart: {
      deep: true,
      handler() {
        if (this.chart.chart_distribution) {
          this.generateSeries()
        }
      },
    },
  },
  data() {
    return {
      options: {
        tooltip: {
          confine: true,
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          textStyle: {
            fontFamily: chartFontFamily,
            lineHeight: 1.15,
          },
          backgroundColor: 'rgba(71, 73, 77, 0.8)',
          padding: 16,
          extraCssText: 'border-radius: 10px;',
        },
        grid: {
          top: '16px',
          left: '16px',
          right: '16px',
          bottom: '38px',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontFamily: chartFontFamily,
            fontSize: chartFontSize,
            color: '#47494D',
          },
          axisLabel: {
            fontFamily: chartFontFamily,
            fontSize: chartFontSize,
          },
        },
        yAxis: {
          position: 'right',
          type: 'category',
          z: 10,
          nameTextStyle: {
            fontFamily: chartFontFamily,
            fontSize: chartFontSize,
            color: '#47494D',
          },
          axisLabel: {
            fontFamily: chartFontFamily,
            fontSize: chartFontSize,
            inside: true,
            textShadowColor: '#000',
            textShadowBlur: 1,
            color: '#fff',
            textBorderWidth: 1,
            textBorderColor: '#000',
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        series: [],
      },
    }
  },
  mounted() {
    if (this.chart.chart_distribution) {
      this.generateSeries()
    }
  },
  methods: {
    generateSeries() {
      let series = []
      let yAxisData = []

      this.chart.chart_distribution.data.forEach(d => {
        yAxisData.push(d.acronym)
      })

      this.chart.chart_distribution.legend.forEach((legend, i) => {
        let data = []
        this.chart.chart_distribution.data.forEach(d => {
          data.push(d.location_distribution[i])
        })

        series.push({
          name: legend.name[this.$i18n.locale] || legend.name.default,
          type: 'bar',
          stack: 'distribution',
          data: data,
          smooth: true,
          itemStyle: {
            color: legend.color,
          },
          barWidth: 20,
        })
      })

      this.options.xAxis.name =
        this.chart.chart_distribution.xAxis.name[this.$i18n.locale] || this.chart.chart_distribution.xAxis.name.default
      this.options.yAxis.data = yAxisData
      this.options.series = series
      this.options.tooltip.formatter = params => {
        const tooltipColor = color =>
          `<span class="analytic-distribution__container__chart__tooltip__item__color" style="background-color:${color}"></span>`
        const brand = this.chart.chart_distribution.data.find(d => d.acronym === params[0].axisValue)
        let tooltip = `
        <div class="analytic-distribution__container__chart__tooltip__title">
          ${brand.name}
        </div>`
        params.forEach(item => {
          tooltip += `
          <div class="analytic-distribution__container__chart__tooltip__item">
            <div class="analytic-distribution__container__chart__tooltip__item--align-start">
              ${tooltipColor(item.color)} ${item.seriesName}
            </div>
            <div class="analytic-distribution__container__chart__tooltip__item--align-end">
              ${item.data}
            </div>
          </div>`
        })

        return `<div class="analytic-distribution__container__chart__tooltip">${tooltip}</div>`
      }
    },
  },
}
</script>

<style lang="scss">
.analytic-distribution {
  @include box;

  &__header {
    @include header;

    &__title {
      @include title;
    }

    &__tooltip {
      margin-left: 8px;
    }
  }

  &__legend {
    padding: $spacing-sm $spacing-sm 0;

    @media (min-width: $screen-sm) {
      padding: $spacing-md $spacing-md 0;
    }
  }

  &__container {
    @include container(0);

    &__chart {
      height: 100%;

      &__tooltip {
        &__title {
          margin-bottom: 4px;
          font-weight: 800;
        }

        &__item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 4px;
          font-size: $font-size-sm;
          font-weight: 400;

          &:last-child {
            margin-bottom: 0;
          }

          &--align-start {
            display: flex;
            align-items: center;
          }

          &--align-end {
            display: flex;
            align-items: flex-end;
            margin-left: 16px;
            font-weight: bold;
          }

          &__color {
            margin-right: 8px;
            border-radius: 25px;
            width: 12px;
            height: 12px;
          }
        }
      }
    }
  }
}
</style>
